// @ts-nocheck
import { addListener, makeEvent } from "~/lib/signals";
import { store } from "~/state/store";
import { CONFIG } from "~/config";
import GPXContent from "~/gpx/content";
import { exclusiveBoxesSetState } from "~/es6/exclusiveboxes";
import DistanceMeterContent from "~/distancemeter-content";
import UserMarksContent from "~/usermarks-content";
import Scene from "~/scene";
import SharePositionContent from "~/share-position/content";
import { default as SharePositionContent2 } from "~/share-position2/content";
import Detail from "~/detail";
import Canonical from "~/canonical";
import { setMapset } from "~/state/map/state";
import { MAP_OVERLAYS, DEFAULT_MAP_STATE } from "~/state/map/constants";
import { openPropositionDialog } from "~/util/premium";
import CouponActivationDialog from "~/ui/dialogs/coupon-activation-dialog";
import { ELoginState } from "~/state/user/interfaces";
import PaddleWrapper from "~/util/paddleWrapper";
import deeplinksConf from "~common/conf/deep-link.json";
import MapControls from "~/scene/control/mapcontrols";
import MiniCzechMap from "./ui/controls/mini-czech-map";
import MiniWorldMap from "./ui/controls/mini-world-map";

/**
 * Volani set state metody nad component + pokud vraci promise a obsahuje metodu historyAfterState,
 * zavolame i ji.
 *
 * @param {Object} item
 * @param {Object} data
 */
function setState(item, data) {
	const output = item.setState(data);

	if (output && typeof output.then === "function" && "historyAfterState" in item) {
		output.then(() => {
			item.historyAfterState(data);
		}, () => {});
	}
};


/**
 * @class Spravce historie: bere data od posluchacu a cpe je do history utilu
 * @signal mapy-history-change signal o tom, ze se pravdepodobne zmenil stav mapy (byl zavolan history.save)
 */
export default class History {
	constructor() {
		this._seo = {
			"planovani-trasy": Mapy.getComponent("route"),
			"moje-mapy": Mapy.getComponent("mymaps"),
			"vlastni-body": Mapy.getComponent("userMarks"),
			"mereni-vzdalenosti": Mapy.getComponent("distanceMeter"),
			"profil": Mapy.getComponent("profile"),
			"sdileni-polohy": Mapy.getComponent("sharePosition"),
		};

		this._state = null;
		this._fromHistory = false;

		this._overlayItems = [DistanceMeterContent, UserMarksContent, Detail, Mapy.UserProfile, GPXContent, SharePositionContent, SharePositionContent2, Mapy.PathContent];
		this._prevSID = null;
		this._prevURL = "";
		this._jakHistory2 = new JAK.History2({
			processor: this,
		});
		addListener(this, "history-change", "_historyChange", this._jakHistory2);
		this._rewriteMode = false;
	}

	getJakHistory2() {
		return this._jakHistory2;
	}

	/**
	 * je z historie?
	 * @return {Boolean}
	 */
	isFromHistory() {
		return this._fromHistory;
	}

	get state() {
		return this._state;
	}

	/**
	 * Ulozeni aktualniho stavu mapy do url.
	 * @param {[Boolean]} replaceParam Prepsat aktualni url?
	 * @return {Boolean} Pri uspesnem ulozeni vraci true, jinak false pri vyjimce.
	 */
	save(replaceParam = false) {
		if (CONFIG.inFrame) {
			return Promise.resolve();
		}

		let replace = replaceParam;

		/* ziskat data od relevantnich komponent */
		let data = {
			...Mapy.getComponent("layout").getState(),
			...Mapy.getComponent("reportDefect").getState(),
			...Mapy.getComponent("dimcurator").getState(),
		};

		const activeOverlay = this._getActiveOverlay();

		if (activeOverlay.length) {
			const state = activeOverlay[0].getState();

			if (Object.keys(state).length) {
				delete data.dim;
			}
			data = Object.assign(data, state);
		} else {
			for (let index = 0; index < Mapy.active.length; index++) {
				data = Object.assign(data, Mapy.active[index].getState());
			}
		}

		if (Mapy.getComponent("gallery").isActive()) {
			Object.assign(data, Mapy.getComponent("gallery").getState());
		}

		if (Mapy.getComponent("photoDetail").isActive()) {
			Object.assign(data, Mapy.getComponent("photoDetail").getState());
		}

		// doplneni dat o to, co mame ve store
		const storeState = store.getState();

		// x, y, z, m3d, yaw, pitch, height, mapset, ovl
		Object.assign(data, {
			x: storeState.map.longitude.toFixed(7),
			y: storeState.map.latitude.toFixed(7),
			z: storeState.map.zoom,
			...storeState.map.provider === "m3d"
				? {
					m3d: 1,
					height: Math.round(storeState.map.height),
					yaw: Mapy.Util.formatNumber(storeState.map.yaw),
					pitch: Mapy.Util.formatNumber(storeState.map.pitch),
				}
				: {},
			mapset: storeState.map.mapset,
			...storeState.map.overlays.length ? { ovl: storeState.map.overlays.join(",") } : {},
		});

		if (this._rewriteMode) {
			replace = true;
		}

		if (this._prevSID) {
			this._prevSID.dontSave = true;
		}

		Mapy.getComponent("languageControl").setUrls(data);

		const obj = {};

		this._prevSID = obj;

		const toDelete = ["searchOrigin", "active"];

		for (const item of toDelete) {
			if (this._state && item in this._state) {
				delete this._state[item];
			}
		}

		// uprava pro wasm zoom
		if (CONFIG.wasmEnabled) {
			data.z = parseFloat(data.z.toFixed(3));
		}

		this._state = data;

		return this._prevURL === this.serialize(data)
			? Promise.resolve()
			: Mapy.SIDCurator.getSIDFromData(data).then(newData => {
				if (!obj.dontSave) {
					this._prevSID = null;
					this._prevURL = this.serialize(data);
					this._jakHistory2.save(newData, replace);
					makeEvent("mapy-history-change", this, {});
				}
			}, () => {
				this._prevURL = this.serialize(data);
				this._jakHistory2.save(data, replace);
				makeEvent("mapy-history-change", this, {});
			});
	}

	/**
	 * spusteni nacitani historie
	 */
	async start() {
		try {
			const event = {
				data: {
					state: {
						...this._jakHistory2.get(),
						mapset: DEFAULT_MAP_STATE.mapset,
						...CONFIG.urlData ? this.parse(CONFIG.urlData) : {},
					},
				},
			};

			// DEEPLIKNS

			const url = new URL(location);
			const checkDeepLink = url.pathname.match(/\/d\/(.*)\??/u);
			const defaultStart = () => {
				Mapy.getComponent("search").activate(); /* to je default, ochrana pred obnovenim z historie na detail */
				Mapy.getComponent("languageControl").setUrls(event.data.state);
				this._historyChange(event);
			};

			if (checkDeepLink && checkDeepLink[1] && deeplinksConf?.[checkDeepLink[1]]?.action) {
				await import(
					/* webpackChunkName: "deeplink" */
					"~/deeplink"
				).then(module => {
					const actionFcName = deeplinksConf[checkDeepLink[1]].action;

					if (module[actionFcName]) {
						module.initModule({
							cml: Mapy.CML,
							route: Mapy.getComponent("route"),
							store,
							search: Mapy.getComponent("search"),
							settings: Mapy.getComponent("settings"),
							mapcontrols: MapControls,
							shareposition: Mapy.getComponent("sharePosition"),
							profile: Mapy.getComponent("profile"),
							login: Mapy.getComponent("login"),
							mymaps: Mapy.getComponent("mymaps"),
							gallery: Mapy.getComponent("gallery"),
							premiumStatus: Mapy.getComponent("premiumStatus"),
							openPropositionDialog,
						});

						module[actionFcName](url.searchParams);

						for (const search in this._jakHistory2.get()) {
							if (search !== "mapset") {
								delete event.data.state[search];
							}
						}
					} else {
						defaultStart();
					}
				}, () => {
					// eslint-disable-next-line no-console
					console.warn("Wont load module Deeplinks");
				});

				Mapy.getComponent("languageControl").setUrls(event.data.state);
			} else {
				defaultStart();
			}
		} catch (ex) {
			// eslint-disable-next-line no-console
			console.log(ex);
		}
	}

	/**
	 * parsovani url stringu
	 * @param  {String} stringParam url
	 * @return {Object} vysledny objekt s daty z url
	 */
	parse(stringParam) {
		const result = {};
		const string = stringParam.split("#")[0];

		let part1 = string;
		let part2 = "";

		const index = string.indexOf("?");

		if (index !== -1) {
			part1 = string.substring(0, index);
			part2 = string.substring(index+1);
		}

		const mapsetParts = part1.substring(1).split("/");
		const mapset = mapsetParts.length ? mapsetParts[mapsetParts.length - 1] : "";

		if (mapset) {
			result.mapset = mapset;
		}
		const parts = part2.split("&");

		parts.forEach(part => {
			const keyvalue = part.split("=");

			if (keyvalue.length < 2 && !result.active) { // prave aktivni content
				result.active = part;

				return;
			}

			try {
				const key = decodeURIComponent(keyvalue[0]);
				const value = decodeURIComponent(keyvalue.slice(1).join("=").replace(/\+/gu, " "));

				if (value === "" && Object.keys(this._seo).indexOf(key) > -1) {
					result.active = key;
				} else if (key in result) {
					let current = result[key];

					if (!(current instanceof Array)) {
						current = [current];
						result[key] = current;
					}
					current.push(value);
				} else {
					result[key] = value;
				}
			} catch (error) {
				//console.log(error);
			}
		});

		return result;
	}

	/**
	 * [serialize description]
	 * @param  {Object} data stav aplikace
	 * @return {String} url
	 */
	serialize(data, onlyComponentData) {
		const state = store.getState();
		const str = [];

		/* posledni aktivni je ten "hlavni" */
		const activeOverlay = this._getActiveOverlay();
		let active = activeOverlay.length ? activeOverlay[0] : Mapy.active[Mapy.active.length-1];

		if (activeOverlay.length && activeOverlay[0] instanceof Detail && state.detail.source === "closure") {
			active = Mapy.getComponent("route");
		}

		if (!onlyComponentData) {
			const seo = this._componentToSeo(active);

			if (seo) {
				str.push(encodeURIComponent(seo));
			}
		}

		for (const param in data) {
			if (param === "mapset") {
				continue;
			}
			const value = [].concat(data[param]);

			for (let index = 0; index < value.length; index++) {
				str.push(`${encodeURIComponent(param)}=${encodeURIComponent(value[index])}`);
			}
		}

		const path = onlyComponentData ? "" : `/${CONFIG.defaultLang}/${data.mapset || ""}`;

		return `${path}${str.length ? `?${str.join("&")}` : ""}`;
	}

	/**
	 * Nastavit stav replace modu u historie.
	 * @param {Boolean} state Zapnuti/vypnuti prepisu historie.
	 */
	setRewriteMode(state) {
		this._rewriteMode = state;
	}

	/**
	 * Získaní stavu replace modu u historie.
	 * @param {Boolean}
	 */
	getRewriteMode() {
		return this._rewriteMode;
	}

	_componentToSeo(component) {
		for (const seo in this._seo) {
			if (this._seo[seo] === component) {
				return seo;
			}
		}

		return "";
	}

	/**
	 * nasatla zmena historie
	 * @param  {Object} event
	 */
	_historyChange(event) {
		const data = event.data.state;

		// #presmerovani starych url
		// presmerovani z planovani okruhu na planovani trasy
		if (data.active === "planovani-okruhu") {
			data.oldSEOUrl = 1;
			data.active = "planovani-trasy";
		}
		// presmerovani stareho batuzku a navstivenych na moje mapy
		if (data.active === "moje" || data.active === "visit") {
			data.active = "moje-mapy";
			data.cat = "mista-trasy";
		}
		// presmerovani fotomap do moje mapy
		if (data.active === "fotomapy") {
			data.active = "moje-mapy";
			data.cat = "fotografie";
			if ("aid" in data && data.aid !== "") {
				data.mid = data.aid;
				delete data.aid;
			}
		}
		// presmerovani stop do moje mapy
		if (data.active === "stopy") {
			data.active = "moje-mapy";
			data.cat = "aktivity";
			if ("tid" in data && data.tid !== "") {
				data.mid = data.tid;
				delete data.tid;
			}
		}
		// prepsani mereni a vlastnich bodu dle nove struktury url (obsolete??)
		if (data.active === "mereni-vlastni-bod") {
			if ("msw" in data) {
				data.active = data.msw;
			}
		}
		// presmerovani na nove detaily Moje mapy
		if (data.active === "moje-mapy" && "dt" in data && data.dt === "item") {
			data.dim = data.mid;
			delete data.dt;
			delete data.mid;
		}
		if (data.active === "moje-mapy" && "dt" in data && data.dt === "track") {
			delete data.dt;
			data.cat = "aktivity";
		}
		// oprava umisteni detailu z mych map a poi detailu v url > prednost dostane poi detail
		if ("dim" in data && "source" in data && "id" in data) {
			delete data.dim;
		}

		// nova galerie
		if ("source" in data && (["foto", "ffoto"].indexOf(data.source) > -1) && "id" in data) {
			data.sourcep = data.source;
			data.idp = data.id;

			delete data.source;
			delete data.id;
			delete data.gallery;
		}

		if ("cat" in data && data.cat === "fotografie" && data.mid) {
			data.source = "author";
			data.id = data.mid;
			data.cat = "mista-trasy";

			delete data.mid;
		}

		// stare ophoto nahradime leteckou s novymi overlays
		if (data.base === "ophoto") {
			if (data.mapset === "dopravni") {
				data.ovl = MAP_OVERLAYS.TRAFFIC.toString();
			} else if (data.mapset === "fotografie") {
				data.ovl = [MAP_OVERLAYS.PHOTO, MAP_OVERLAYS.TURIST].toString();
			} else {
				data.ovl = MAP_OVERLAYS.TURIST.toString();
			}
			data.mapset = "letecka";
			delete data.base;
		}

		// starou fotografickou nahodime jako turistickou s fotkami jako overlayem
		if (data.mapset === "fotografie") {
			data.mapset = "turisticka";
			data.ovl = MAP_OVERLAYS.PHOTO.toString();
		}

		// starou katastralni nahodime jako leteckou s katastralnim overlayem
		if (data.mapset === "katastralni") {
			data.mapset = "letecka";
			data.ovl = MAP_OVERLAYS.CADASTRAL.toString();
		}

		// ptaci pohled zmenime na pohled na leteckou mapu
		if ("or" in data && "oc" in data) {
			data.mapset = "letecka";
			delete data.or;
			delete data.oc;
		}

		// #presmerovani starych url - konec

		// pokud se chci koukat na vlastni verejny profil, mam smulu a jdu do MM
		const state = store.getState();
		const { user } = state.user;

		if (user.state === "green" && user.userPublicId === data.up) {
			data.active = "moje-mapy";

			switch (data.upc) {
				case "reviews":
					data.cat = "reviews";
					break;
				default:
					data.cat = "dashboard";
					break;
			}

			delete data.up;
			delete data.upc;
		}

		// pro analytiku si posleme jeste, ze jde o akci vyvolanou zmenou URL
		data.searchOrigin = "browser_history";

		this._fromHistory = true;

		const active = this._seo[data.active] || Mapy.getComponent("search");
		const allComponents = [Mapy].concat(Mapy.active).concat(active);

		[
			"userProfile",
			"detail",
			"pano",
			"gallery",
			"activerequest",
			"timeboard",
			"webcam",
			"reportDefect",
			"legend",
			"mobilepromo",
			"landingPagePromo",
			"tweaksList",
			"layout",
			"sharePosition",
		].forEach(name => {
			allComponents.push(Mapy.getComponent(name));
		});

		allComponents.push(Mapy.getComponent("photoDetail"));

		/* pokud je nejaka komponenta v poli dvakrat postarame se o jeji odstraneni.
			* slo predevsim o detail, ktery se setoval dvakrat a delalo to problemy loaderu */
		const components = allComponents.filter((com, pos) => allComponents.indexOf(com) === pos);
		const activeOverlay = this._overlayItems.filter(item => active instanceof item);

		const postPhase = activeOverlay.length || "dim" in data || data.active === "planovani-trasy";

		if (!postPhase && !active.isActive()) {
			active.activate();
		}

		const setComponentState = stateData => {
			for (let index = 0; index < components.length; index++) {
				const item = components[index];

				if (!item) {
					continue;
				}

				setState(item, stateData);
			}

			// exclusive boxes
			exclusiveBoxesSetState(stateData);

			if (postPhase) {
				if ("dim" in stateData) { // pokud mame dim v url, nacitame pomoci dimcuratoru
					Mapy.getComponent("dimcurator").setState({ dim: stateData.dim }, stateData).loadData().then(() => {
						this._fromHistory = false;
					}, () => {
						this._fromHistory = false;
					});
				} else {
					active.activate();
					this._fromHistory = false;
				}
			} else {
				this._fromHistory = false;
			}
		};

		if ("sid" in data) { // pokud mame sid v url, nacitame pomoci sidcuratoru
			Mapy.ProviderSID.getData(data.sid).then(response => {
				const sidData = this.parse(response.url);

				if (typeof sidData.mapset === "string" && !sidData.mapset) {
					delete sidData.mapset;
				}

				this._state = {
					...data,
					...sidData,
				};
				setComponentState(this._state);
			}, () => {
				this._state = data;
				setComponentState(this._state);
			});
		} else {
			this._state = data;
			setComponentState(this._state);
		}

		if ("om" in data && Scene) {
			const type = parseInt(data.om);

			switch (type) {
				case 1: {
					const czechMap = new MiniCzechMap();

					document.body.append(czechMap.container);
					break;
				}

				case 2: {
					const worldMap = new MiniWorldMap();

					document.body.append(worldMap.container);
					break;
				}

				default:
			}
		}

		// pokud mame stranku s uzivatelskym profilem a pridruzene pod stranky, tak nechceme indexovat
		if (data.up && !document.head.querySelector("meta[name=robots]")) {
			const metaRobots = document.createElement("meta");

			metaRobots.setAttribute("name", "robots");
			metaRobots.setAttribute("content", "noindex");
			document.head.appendChild(metaRobots);

			Canonical.remove();
		}

		// otevreme premium propozici, kdyz najdeme prislusny atribut v url
		if ("proposition" in data) {
			if (user.premium) {
				Mapy.getComponent("premiumStatus").activate();
			} else {
				openPropositionDialog("url");
			}

			PaddleWrapper.initialize();
		}

		// pokud jdeme na URL s id kuponu, tak rovnou zacneme proces prijmuti premia
		if ("premium-coupon-id" in data) {
			// pokud uzivatel neni prihlaseny, tak ho presmerujeme na login
			if (user.state !== ELoginState.Green) {
				const url = new URL(`https://login.szn.${CONFIG.TLDCZ}`);
				const returnUrl = new URL((window as Window & typeof globalThis).location.href);

				Array.from(returnUrl.searchParams.keys()).forEach(key => returnUrl.searchParams.delete(key));
				returnUrl.searchParams.set("premium-coupon-id", data["premium-coupon-id"]);
				url.searchParams.set("return_url", returnUrl.toString());
				url.searchParams.set("serviceId", "mapy");

				(window as Window & typeof globalThis).location.href = url.toString();

				return;
			}

			let couponDialog: CouponActivationDialog = null;
			const dispose = () => {
				couponDialog?.destroy();
				couponDialog = null;
			};

			couponDialog = new CouponActivationDialog({
				onClose: dispose,
				onBack: dispose,
				onFinished: async couponData => {
					dispose();
					await Mapy.getComponent("login").relogin();

					Mapy.getComponent("logger").log("premiumPurchase_success", {
						purchaseOrigin: couponData?.subscriptions?.length ? couponData.subscriptions[0].subscriptionOrigin : "coupon",
					});

					Mapy.getComponent("premiumStatus").activate();
				},
				couponId: data["premium-coupon-id"],
			});
		}

		const mapset = data.mapset || state.map.mapset;
		let overlays = (data.ovl as string)?.split(",").map(overlay => parseInt(overlay)).filter(number => !isNaN(number)) || state.map.overlays;

		// fix bezkarskych tras v URL, ktera neni ale zimni mapa
		if (mapset !== "zimni" && overlays.indexOf(MAP_OVERLAYS.SKI) > -1) {
			overlays = [];
		}

		store.dispatch(setMapset({
			mapset,
			overlays,
		}));
	}

	/**
	 * navrati pole s aktivnima overlayema
	 * @return {Array} pole s overlayema
	 */
	_getActiveOverlay() {
		const output = [];

		for (let index = 0, max = Mapy.active.length; index < max; index++) {
			const active = Mapy.active[index];

			for (let itemIndex = 0, max2 = this._overlayItems.length; itemIndex < max2; itemIndex++) {
				const overlayItem = this._overlayItems[itemIndex];

				if (active instanceof overlayItem) {
					output.push(active);
				}
			}
		}

		return output;
	}
}
